import { useState, useMemo, useContext } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Settings, Cookie, Shield, BarChart3, Megaphone, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useCookieConsent, type CookieConsents } from "@/hooks/useCookieConsent";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations } from "@/i18n/translations";

function useSafeLanguage() {
  try {
    return useLanguage();
  } catch {
    return { t: translations.en };
  }
}

export function CookieConsentBanner() {
  const { hasConsented, acceptAll, rejectAll, acceptSelected } = useCookieConsent();
  const { t } = useSafeLanguage();
  const cc = t.cookieConsent!;
  const [showSettings, setShowSettings] = useState(false);
  const [selected, setSelected] = useState<CookieConsents>({
    functional: false,
    analytics: false,
    marketing: false,
  });

  const categories = useMemo(() => [
    { key: "necessary" as const, label: cc.necessaryLabel, description: cc.necessaryDescription, icon: Shield, locked: true },
    { key: "functional" as const, label: cc.functionalLabel, description: cc.functionalDescription, icon: Wrench, locked: false },
    { key: "analytics" as const, label: cc.analyticsLabel, description: cc.analyticsDescription, icon: BarChart3, locked: false },
    { key: "marketing" as const, label: cc.marketingLabel, description: cc.marketingDescription, icon: Megaphone, locked: false },
  ], [cc]);

  if (hasConsented) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-[100] border-t border-border/60 bg-card/95 backdrop-blur-xl shadow-[0_-8px_30px_rgba(0,0,0,0.4)]"
      >
        <div className="mx-auto max-w-4xl px-4 py-5 sm:px-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Cookie className="h-5 w-5 text-primary" />
                {cc.title}
              </h3>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                {cc.description}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={acceptAll} className="flex-1 font-medium">
                {cc.acceptAll}
              </Button>
              <Button onClick={rejectAll} variant="outline" className="flex-1 font-medium">
                {cc.rejectAll}
              </Button>
              <Button
                onClick={() => setShowSettings((v) => !v)}
                variant="secondary"
                className="flex-1 font-medium gap-2"
              >
                <Settings className="h-4 w-4" />
                {cc.managePreferences}
              </Button>
            </div>

            <AnimatePresence>
              {showSettings && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="pt-3 border-t border-border/40 space-y-3">
                    <p className="text-sm text-muted-foreground">
                      {cc.settingsDescription}
                    </p>

                    <div className="space-y-2">
                      {categories.map((cat) => {
                        const Icon = cat.icon;
                        const isOn = cat.locked || selected[cat.key as keyof CookieConsents] || false;

                        return (
                          <div
                            key={cat.key}
                            className="flex items-center justify-between gap-4 rounded-lg bg-muted/40 px-4 py-3"
                          >
                            <div className="flex items-start gap-3 min-w-0">
                              <Icon className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                              <div className="min-w-0">
                                <span className="text-sm font-medium text-foreground">{cat.label}</span>
                                <p className="text-xs text-muted-foreground mt-0.5">{cat.description}</p>
                              </div>
                            </div>
                            <Switch
                              checked={isOn}
                              disabled={cat.locked}
                              onCheckedChange={(v) =>
                                !cat.locked &&
                                setSelected((s) => ({ ...s, [cat.key]: v }))
                              }
                              className="shrink-0"
                            />
                          </div>
                        );
                      })}
                    </div>

                    <Button
                      onClick={() => acceptSelected(selected)}
                      className="w-full font-medium"
                    >
                      {cc.confirmSelection}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex gap-3 text-xs text-muted-foreground pt-1">
              <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground transition-colors">
                {cc.privacyPolicy}
              </Link>
              <span>|</span>
              <Link to="/imprint" className="underline underline-offset-2 hover:text-foreground transition-colors">
                {cc.imprint}
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/** Persistent small button (bottom-left) to re-open consent manager */
export function CookieSettingsButton() {
  const { hasConsented, resetConsent } = useCookieConsent();
  const { t } = useSafeLanguage();

  if (!hasConsented) return null;

  return (
    <button
      onClick={resetConsent}
      aria-label={t.cookieConsent?.settingsAriaLabel ?? "Cookie settings"}
      className="fixed bottom-4 left-4 z-[99] flex h-10 w-10 items-center justify-center rounded-full bg-card/90 border border-border/60 shadow-lg backdrop-blur-sm transition-colors hover:bg-muted"
    >
      <Cookie className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}
